import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import toast from 'react-hot-toast'
import { FaFileExcel, FaSearch, FaUpload } from 'react-icons/fa'
import { fetchData, downloadFile } from '../../../helpers/fetchData'
import ReusableModal from '../../../shared/ReuseableModal'
import DatePicker from '../../../shared/DatePicker'
import CustomButton from '../../../shared/CustomButton'
import CustomTabs from '../../../shared/CustomTabs'
import UploadExcelFile from '../../../shared/UploadExcelFile'
import CheckPermission from '../../../hooks/CheckPermission'
import ConfirmationModal from '../../../shared/ConfirmationModal'

const getStatusColors = status => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return {
        pill: 'bg-green-500 text-white',
        bar: 'bg-green-500'
      }
    case 'failed':
      return {
        pill: 'bg-red-500 text-white',
        bar: 'bg-red-500'
      }
    case 'running':
    case 'processing':
      return {
        pill: 'bg-amber-500 text-white',
        bar: 'bg-blue'
      }
    default:
      return {
        pill: 'bg-gray-500 text-white',
        bar: 'bg-blue'
      }
  }
}

const BulkSfdaAutoCorrectionModal = ({ isVisible, onClose, onSuccess }) => {
  const { hasPermission } = CheckPermission()
  const canManageSfda = hasPermission('cliamgeneration', 'update') || hasPermission('sfda', 'update') || hasPermission('cliamgeneration', 'create') || hasPermission('sfda', 'create')
  const { t } = useTranslation()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadStatusLoading, setUploadStatusLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [jobId, setJobId] = useState('')
  const [jobStatus, setJobStatus] = useState(null)
  const [uploadJobId, setUploadJobId] = useState('')
  const [uploadJobStatus, setUploadJobStatus] = useState(null)
  const [file, setFile] = useState(null)
  const [hasAutoDownloaded, setHasAutoDownloaded] = useState(false)
  const [auditCompletionHandled, setAuditCompletionHandled] = useState(false)
  const [uploadCompletionHandled, setUploadCompletionHandled] = useState(false)
  const [showAuditConfirm, setShowAuditConfirm] = useState(false)
  const [showUploadConfirm, setShowUploadConfirm] = useState(false)
  const [formData, setFormData] = useState({
    start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD')
  })

  const progressValue = useMemo(() => {
    const rawProgress = Number(jobStatus?.progress_percentage || 0)
    if ((jobStatus?.status || '').toLowerCase() === 'completed') return 100
    return Math.min(Math.max(rawProgress, 0), 100)
  }, [jobStatus?.progress_percentage, jobStatus?.status])

  const uploadProgressValue = useMemo(() => {
    if ((uploadJobStatus?.status || '').toLowerCase() === 'completed') return 100
    return 0
  }, [uploadJobStatus?.status])

  const summaryStats = useMemo(() => {
    const summary = jobStatus?.sfda_audit_summary || {}
    return {
      totalItems: Number(summary.total_items || jobStatus?.total_audits || 0),
      validItems: Number(summary.valid_items || 0),
      invalidItems: Number(summary.invalid_items || 0),
      emptyCodes: Number(summary.empty_codes || 0),
      exportedRows: Number(summary.exported_rows || 0)
    }
  }, [jobStatus])

  const uploadStats = useMemo(() => {
    const stats = uploadJobStatus?.stats || {}
    return {
      totalRows: Number(stats.total_rows || 0),
      updatedRows: Number(stats.updated_rows || 0),
      skippedRows: Number(stats.skipped_rows || 0),
      updatedDocs: Number(stats.updated_docs || 0)
    }
  }, [uploadJobStatus])

  const summaryCards = useMemo(
    () => [
      {
        key: 'total',
        label: t('Total Latest Codes'),
        value: summaryStats.totalItems,
        className: 'bg-sky-50 text-sky-500'
      },
      {
        key: 'valid',
        label: t('Valid Codes'),
        value: summaryStats.validItems,
        className: 'bg-green-50 text-green-600'
      },
      {
        key: 'invalid',
        label: t('Invalid Codes'),
        value: summaryStats.invalidItems,
        className: 'bg-red-50 text-red-500'
      },
      {
        key: 'empty',
        label: t('Empty Codes'),
        value: summaryStats.emptyCodes,
        className: 'bg-amber-50 text-amber-600'
      }
    ],
    [summaryStats, t]
  )

  const uploadCards = useMemo(
    () => [
      {
        key: 'totalRows',
        label: t('Total Rows'),
        value: uploadStats.totalRows,
        className: 'bg-sky-50 text-sky-500'
      },
      {
        key: 'updatedRows',
        label: t('Updated Rows'),
        value: uploadStats.updatedRows,
        className: 'bg-green-50 text-green-600'
      },
      {
        key: 'skippedRows',
        label: t('Skipped Rows'),
        value: uploadStats.skippedRows,
        className: 'bg-amber-50 text-amber-600'
      },
      {
        key: 'updatedDocs',
        label: t('Updated Items'),
        value: uploadStats.updatedDocs,
        className: 'bg-blue-50 text-blue-600'
      }
    ],
    [uploadStats, t]
  )

  const statusColors = useMemo(
    () => getStatusColors(jobStatus?.status),
    [jobStatus?.status]
  )

  const uploadStatusColors = useMemo(
    () => getStatusColors(uploadJobStatus?.status),
    [uploadJobStatus?.status]
  )

  const handleDateChange = event => {
    const { name, value } = event.target || {}
    setFormData(prev => ({
      ...prev,
      [name]: moment(value).format('YYYY-MM-DD')
    }))
  }

  const fetchAuditJobStatus = async currentJobId => {
    if (!currentJobId) return null

    setStatusLoading(true)
    try {
      const response = await fetchData(
        'GET',
        `/v3/invoices/audit/sfda-audit/job/${currentJobId}`
      )
      if (response) {
        setJobStatus(response)
        return response
      }
      return null
    } finally {
      setStatusLoading(false)
    }
  }

  const fetchUploadJobStatus = async currentJobId => {
    if (!currentJobId) return null

    setUploadStatusLoading(true)
    try {
      const response = await fetchData(
        'GET',
        `/v3/migration/sfda-register-numbers/upload-excel/job/${currentJobId}`
      )
      if (response) {
        setUploadJobStatus(response)
        return response
      }
      return null
    } finally {
      setUploadStatusLoading(false)
    }
  }

  useEffect(() => {
    if (!isVisible || !jobId) return undefined

    let intervalId = null
    const poll = async () => {
      const response = await fetchAuditJobStatus(jobId)
      const status = response?.status?.toLowerCase()
      if (status === 'completed' || status === 'failed') {
        if (intervalId) clearInterval(intervalId)
      }
    }

    poll()
    intervalId = setInterval(poll, 4000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isVisible, jobId])

  useEffect(() => {
    if (!isVisible || !uploadJobId) return undefined

    let intervalId = null
    const poll = async () => {
      const response = await fetchUploadJobStatus(uploadJobId)
      const status = response?.status?.toLowerCase()
      if (status === 'completed' || status === 'failed') {
        if (intervalId) clearInterval(intervalId)
      }
    }

    poll()
    intervalId = setInterval(poll, 4000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isVisible, uploadJobId])

  useEffect(() => {
    const status = (jobStatus?.status || '').toLowerCase()
    if (
      !jobStatus ||
      auditCompletionHandled ||
      (status !== 'completed' && status !== 'failed')
    ) {
      return
    }

    setAuditCompletionHandled(true)

    if (status === 'failed') {
      toast.error(jobStatus?.error_message || t('SFDA audit failed'))
      return
    }

    onSuccess?.(jobStatus)

    if (jobStatus?.download_url) {
      const fileName = jobStatus?.file_name || 'sfda_audit.xlsx'
      setDownloading(true)
      downloadFile(
        'GET',
        jobStatus.download_url.replace('/api', ''),
        {},
        fileName
      )
        .then(() => {
          setHasAutoDownloaded(true)
          toast.success(
            t('SFDA audit completed and Excel downloaded successfully')
          )
        })
        .finally(() => {
          setDownloading(false)
        })
      return
    }

    toast.success(
      jobStatus?.error_message || t('SFDA audit completed successfully')
    )
  }, [auditCompletionHandled, jobStatus, onSuccess, t])

  useEffect(() => {
    const status = (uploadJobStatus?.status || '').toLowerCase()
    if (
      !uploadJobStatus ||
      uploadCompletionHandled ||
      (status !== 'completed' && status !== 'failed')
    ) {
      return
    }

    setUploadCompletionHandled(true)

    if (status === 'failed') {
      toast.error(uploadJobStatus?.error || t('SFDA upload job failed'))
      return
    }

    toast.success(t('SFDA upload job completed successfully'))
    onSuccess?.(uploadJobStatus)
    setFile(null)
  }, [onSuccess, t, uploadCompletionHandled, uploadJobStatus])

  const handleStartAuditClick = () => {
    if (!canManageSfda) return
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
      toast.error(t('Start date must be before end date'))
      return
    }

    setShowAuditConfirm(true)
  }

  const handleSubmitAudit = async () => {
    if (!canManageSfda) return
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
      toast.error(t('Start date must be before end date'))
      return
    }

    setLoading(true)
    setShowAuditConfirm(false)
    const response = await fetchData(
      'POST',
      '/v3/invoices/audit/sfda-audit/bulk',
      {
        audit_type: 'audit_by_date_range',
        selected_category: 'medication-codes',
        start_date: formData.start_date,
        end_date: formData.end_date
      }
    )
    setLoading(false)

    if (response?.job_id) {
      setJobId(response.job_id)
      setHasAutoDownloaded(false)
      setAuditCompletionHandled(false)
      setJobStatus({
        job_id: response.job_id,
        status: response.status || 'queued',
        created_at: response.created_at,
        download_url: `/v3/invoices/audit/sfda-audit/job/${response.job_id}/download`,
        sfda_audit_summary: {
          audit_type: response.audit_type,
          selected_category: 'medication-codes'
        }
      })
      toast.success(response.message || t('SFDA audit started successfully'))
    }
  }

  const handleUploadClick = () => {
    if (!canManageSfda) return
    if (!file) {
      toast.error(t('please_select_file_to_upload'))
      return
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
      toast.error(t('Start date must be before end date'))
      return
    }

    setShowUploadConfirm(true)
  }

  const handleUpload = async () => {
    if (!canManageSfda) return
    if (!file) {
      toast.error(t('please_select_file_to_upload'))
      return
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
      toast.error(t('Start date must be before end date'))
      return
    }

    const uploadFormData = new FormData()
    uploadFormData.append('file', file)
    uploadFormData.append('from_date', formData.start_date)
    uploadFormData.append('to_date', formData.end_date)

    setUploadLoading(true)
    setShowUploadConfirm(false)
    const response = await fetchData(
      'POST',
      '/v3/migration/sfda-register-numbers/upload-excel',
      uploadFormData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    setUploadLoading(false)

    if (response?.job_id) {
      setUploadJobId(response.job_id)
      setUploadCompletionHandled(false)
      setUploadJobStatus({
        job_id: response.job_id,
        status: response.status || 'queued',
        created_at: response.created_at,
        from_date: response.from_date,
        to_date: response.to_date,
        dry_run: response.dry_run
      })
      toast.success(response.message || t('SFDA upload job started successfully'))
    }
  }

  const handleDownload = async () => {
    const downloadUrl =
      jobStatus?.download_url ||
      (jobId ? `/v3/invoices/audit/sfda-audit/job/${jobId}/download` : '')

    if (!downloadUrl) {
      toast.error(t('Download URL is not available yet'))
      return
    }

    setDownloading(true)
    await downloadFile(
      'GET',
      downloadUrl.replace('/api', ''),
      {},
      jobStatus?.file_name || 'sfda_audit.xlsx'
    )
    setHasAutoDownloaded(true)
    setDownloading(false)
  }

  const handleRefreshStatus = async () => {
    if (!jobId) return
    await fetchAuditJobStatus(jobId)
  }

  const handleRefreshUploadStatus = async () => {
    if (!uploadJobId) return
    await fetchUploadJobStatus(uploadJobId)
  }

  const resetState = () => {
    setActiveTabIndex(0)
    setLoading(false)
    setStatusLoading(false)
    setUploadLoading(false)
    setUploadStatusLoading(false)
    setDownloading(false)
    setJobId('')
    setJobStatus(null)
    setUploadJobId('')
    setUploadJobStatus(null)
    setFile(null)
    setHasAutoDownloaded(false)
    setAuditCompletionHandled(false)
    setUploadCompletionHandled(false)
    setFormData({
      start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD')
    })
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const renderAuditTab = () => (
    <div className='flex flex-col gap-4 pt-1'>
      <div className='grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-2'>
        <DatePicker
          label={t('Start Date')}
          name='start_date'
          value={
            formData.start_date
              ? moment(formData.start_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select Start Date')}
          disabled={loading || !!jobId}
        />
        <DatePicker
          label={t('End Date')}
          name='end_date'
          value={
            formData.end_date
              ? moment(formData.end_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select End Date')}
          disabled={loading || !!jobId}
        />
      </div>

      {jobStatus && (
        <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='space-y-1'>
              <h3 className='text-xl font-semibold text-gray-800'>
                {t('Batch Status')}
              </h3>
              <div>
                <div className='text-xs text-gray-500'>{t('Job ID')}</div>
                <div className='font-mono text-sm text-gray-800 break-all'>
                  {jobStatus.job_id || jobId}
                </div>
              </div>
            </div>
            <span
              className={`inline-flex items-center rounded-full px-4 py-1 text-sm font-semibold capitalize ${statusColors.pill}`}
            >
              {t(jobStatus.status || 'queued')}
            </span>
          </div>

          <div className='mt-4'>
            <div className='mb-2 flex items-center justify-between gap-3 text-sm'>
              <span className='font-medium text-gray-700'>{t('Progress')}</span>
              <span className='font-medium text-gray-600'>
                {progressValue.toFixed(1)}%
              </span>
            </div>
            <div className='h-5 overflow-hidden rounded-md bg-[#d9e2ff]'>
              <div
                className={`h-full rounded-md transition-all duration-500 ${statusColors.bar}`}
                style={{ width: `${Math.max(progressValue, 0)}%` }}
              />
            </div>
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4'>
            {summaryCards.map(card => (
              <div key={card.key} className={`rounded-lg p-3 ${card.className}`}>
                <div className='text-sm text-gray-500'>{card.label}</div>
                <div className='text-3xl font-semibold leading-tight'>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2'>
            <div>
              <span className='text-gray-600'>{t('Audit Type')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {jobStatus?.sfda_audit_summary?.audit_type || 'audit_by_date_range'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Exported Rows')}:</span>
              <span className='ml-2 font-medium text-green-700'>
                {summaryStats.exportedRows}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Created At')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {jobStatus.created_at || jobStatus.completed_at || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Auto Download')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {hasAutoDownloaded ? t('Completed') : t('Pending')}
              </span>
            </div>
          </div>

          {jobStatus?.error_message && (
            <div className='mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700'>
              {jobStatus.error_message}
            </div>
          )}

          <div className='mt-4 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4'>
            <CustomButton
              label={t('Refresh Status')}
              buttonType='reset'
              icon='pi pi-refresh'
              onClick={handleRefreshStatus}
              disabled={statusLoading || downloading}
              loading={statusLoading}
              className='w-fit'
            />
            <CustomButton
              label={t('Download Excel')}
              buttonType='success'
              icon={FaFileExcel}
              onClick={handleDownload}
              disabled={
                downloading ||
                !['completed'].includes((jobStatus.status || '').toLowerCase()) ||
                !jobStatus?.download_url
              }
              loading={downloading}
              className='w-fit'
            />
          </div>
        </div>
      )}
    </div>
  )

  const renderUploadTab = () => (
    <div className='flex flex-col gap-4 pt-1'>
      <div className='rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-blue-900'>
        {t(
          'Upload the edited SFDA audit template here. The upload uses the same date range to update medication-codes items from the old values to the new values you entered in the Excel file.'
        )}
      </div>

      <div className='grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-2'>
        <DatePicker
          label={t('From Date')}
          name='start_date'
          value={
            formData.start_date
              ? moment(formData.start_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select Start Date')}
          disabled={uploadLoading || !!uploadJobId}
        />
        <DatePicker
          label={t('To Date')}
          name='end_date'
          value={
            formData.end_date
              ? moment(formData.end_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select End Date')}
          disabled={uploadLoading || !!uploadJobId}
        />
      </div>

      <div className='rounded-xl border border-gray-200 bg-white p-4'>
        <UploadExcelFile
          isLoading={uploadLoading || !!uploadJobId}
          file={file}
          setFile={setFile}
        />
      </div>

      {uploadJobStatus && (
        <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='space-y-1'>
              <h3 className='text-xl font-semibold text-gray-800'>
                {t('Upload Job Status')}
              </h3>
              <div>
                <div className='text-xs text-gray-500'>{t('Job ID')}</div>
                <div className='font-mono text-sm text-gray-800 break-all'>
                  {uploadJobStatus.job_id || uploadJobId}
                </div>
              </div>
            </div>
            <span
              className={`inline-flex items-center rounded-full px-4 py-1 text-sm font-semibold capitalize ${uploadStatusColors.pill}`}
            >
              {t(uploadJobStatus.status || 'queued')}
            </span>
          </div>

          <div className='mt-4'>
            <div className='mb-2 flex items-center justify-between gap-3 text-sm'>
              <span className='font-medium text-gray-700'>{t('Progress')}</span>
              <span className='font-medium text-gray-600'>
                {uploadProgressValue.toFixed(1)}%
              </span>
            </div>
            <div className='h-5 overflow-hidden rounded-md bg-[#d9e2ff]'>
              <div
                className={`h-full rounded-md transition-all duration-500 ${uploadStatusColors.bar}`}
                style={{ width: `${Math.max(uploadProgressValue, 0)}%` }}
              />
            </div>
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4'>
            {uploadCards.map(card => (
              <div key={card.key} className={`rounded-lg p-3 ${card.className}`}>
                <div className='text-sm text-gray-500'>{card.label}</div>
                <div className='text-3xl font-semibold leading-tight'>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2'>
            <div>
              <span className='text-gray-600'>{t('From Date')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.from_date || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('To Date')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.to_date || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Created At')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.created_at || uploadJobStatus.completed_at || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Dry Run')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.dry_run ? t('Yes') : t('No')}
              </span>
            </div>
          </div>

          {uploadJobStatus?.error && (
            <div className='mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
              {uploadJobStatus.error}
            </div>
          )}

          <div className='mt-4 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4'>
            <CustomButton
              label={t('Refresh Status')}
              buttonType='reset'
              icon='pi pi-refresh'
              onClick={handleRefreshUploadStatus}
              disabled={uploadStatusLoading}
              loading={uploadStatusLoading}
              className='w-fit'
            />
          </div>
        </div>
      )}
    </div>
  )

  const tabs = [
    {
      name: t('Audit SFDA'),
      content: renderAuditTab
    },
    {
      name: t('Update SFDA by Excel'),
      content: renderUploadTab
    }
  ]

  return (
    <ReusableModal
      isVisible={isVisible}
      title={t('Audit SFDA')}
      HeaderIcon={FaSearch}
      onClose={handleClose}
      className='w-full lg:w-[1080px]'
      position='top'
    >
      <div className='flex flex-col gap-4'>
        <CustomTabs
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          setActiveTabIndex={setActiveTabIndex}
          className='w-full'
          tabClassName='!bg-white'
          panelClassName='!bg-transparent'
        />

        <div className='flex justify-end gap-2 border-t pt-2'>
          <CustomButton
            label={t('Close')}
            buttonType='reset'
            onClick={handleClose}
            disabled={
              loading ||
              statusLoading ||
              uploadLoading ||
              uploadStatusLoading ||
              downloading
            }
            className='w-fit'
            icon='pi pi-times'
          />
          {activeTabIndex === 1 ? (
            canManageSfda && (
              <CustomButton
                label={t('Upload SFDA Excel')}
                buttonType='blue'
                icon={FaUpload}
                onClick={handleUploadClick}
                disabled={uploadLoading || !!uploadJobId || !file}
                loading={uploadLoading}
                className='w-fit'
              />
            )
          ) : (
            !jobId && canManageSfda && (
              <CustomButton
                label={t('Start SFDA Job')}
                buttonType='blue'
                icon='pi pi-play'
                onClick={handleStartAuditClick}
                disabled={loading}
                loading={loading}
                className='w-fit'
              />
            )
          )}
        </div>
      </div>
      <ConfirmationModal
        visible={showAuditConfirm}
        setVisible={setShowAuditConfirm}
        header={t('Start SFDA Job')}
        message={t('Are you sure you want to start SFDA Audit for the selected date range ({{startDate}} to {{endDate}})?', {
          startDate: moment(formData.start_date).format('DD/MM/YYYY'),
          endDate: moment(formData.end_date).format('DD/MM/YYYY')
        })}
        Icon={FaSearch}
        acceptLabel={t('Start')}
        rejectLabel={t('Cancel')}
        isLoading={loading}
        position='top'
        onAccept={handleSubmitAudit}
        onReject={() => setShowAuditConfirm(false)}
      />
      <ConfirmationModal
        visible={showUploadConfirm}
        setVisible={setShowUploadConfirm}
        header={t('Upload SFDA Excel')}
        message={t('Are you sure you want to process this Excel file? It will update SFDA medication codes for the selected date range.')}
        Icon={FaUpload}
        acceptLabel={t('Upload')}
        rejectLabel={t('Cancel')}
        isLoading={uploadLoading}
        position='top'
        onAccept={handleUpload}
        onReject={() => setShowUploadConfirm(false)}
      />
    </ReusableModal>
  )
}

export default BulkSfdaAutoCorrectionModal
